import { ICaseModel, ICaseSearchModel } from '../../../interfaces/ICase';
import { Loader, LoaderAnimation } from '../../shared/Loader';
import { Button, Modal, Stack } from 'react-bootstrap';
import { SortColumns, SortDirections } from '../../../utilities/Constants';
import { useEffect, useState } from 'react';

import { CheckmatePagination } from '../../../pages/quad/CheckmatePagination';
import { IBasePagedModel } from '../../../interfaces/PagedSortedSearch';
import { IValidation } from '../../../interfaces/IError';
import ValidateUtils from '../../../shared/validations';
import { useDidUpdate } from '@mantine/hooks';
import { useZoneContext } from '../../../contexts/useZoneContext';

const PageSize = 10;

export interface IFindCaseModalProps {
    onCancel: () => void;
    onSelectCase: (caseGuid: string) => void;
    open: boolean;
    searchString: string;
}
export function FindCaseModal(props: IFindCaseModalProps) {
    const zoneContext = useZoneContext();
    const [pendingResponse, setPendingResponse] = useState(true);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [totalCaseCount, setTotalCaseCount] = useState(0);
    const [validation, setValidation] = useState<IValidation>({});
    const [caseSearchResults, setCaseSearchResults] = useState<ICaseModel[]>([]);

    const [pagedModel, setPagedModel] = useState<IBasePagedModel>({
        pageNumber: 0,
        pageSize: PageSize,
        totalPages: 0,
        totalResults: 0,
    });

    const refreshCaseCount = async () => {
        const res = await zoneContext.zoneApiClient.getCaseCount({
            searchText: props.searchString,
        } as ICaseSearchModel);

        if (res.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (res.errorMessage) {
            setValidation(ValidateUtils.parseErrors(res.errors, res.errorMessage));
            setPendingResponse(false);
            return;
        }

        if (res.payload) {
            setTotalCaseCount(res.payload.data);
        }
    };

    const runSearch = async (searchString: string, newSearch?: boolean) => {
        const validation: IValidation = {};

        if (!props.searchString || props.searchString.trim() === '') {
            validation.caseNameOrNumberSearch = ['Enter a Case Number or Case Name'];
            setValidation(validation);
            return;
        } else {
            validation.caseNameOrNumberSearch = [''];
        }

        if (newSearch) await refreshCaseCount();

        const caseSearchParameters: ICaseSearchModel = {
            searchText: searchString,
            queryModel: {
                pageNumber: pagedModel.pageNumber - 1 < 0 ? 0 : pagedModel.pageNumber - 1, // API expects 0-based index
                pageSize: PageSize,
                sortDirection: SortDirections.Asc,
                sortBy: SortColumns.CaseName,
            },
        };

        setPendingResponse(true);
        const res = await zoneContext.zoneApiClient.getCases(caseSearchParameters);
        if (res.httpResponse.status == 401) {
            window.location.reload();
            return false;
        }

        if (res.errorMessage) {
            setValidation(ValidateUtils.parseErrors(res.errors, res.errorMessage));
            setPendingResponse(false);
            return false;
        }

        setInitialDataLoaded(true);
        setCaseSearchResults(res.payload!);
        setValidation(validation);
        setPendingResponse(false);
    };

    useDidUpdate(() => {
        runSearch(props.searchString);
    }, [pagedModel]);

    useDidUpdate(() => {
        setPagedModel((prev) => ({
            ...prev,
            pageNumber: 1, // Reset to first page on total case count change
            totalResults: totalCaseCount,
            totalPages: Math.ceil(totalCaseCount / PageSize),
            recordStartIndex: 0 * PageSize + 1,
            recordEndIndex: 0 * PageSize + PageSize,
        }));
    }, [totalCaseCount, setPagedModel]);

    useEffect(() => {
        runSearch(props.searchString, true);
    }, []);

    const isInitialLoad = pendingResponse && !initialDataLoaded;

    return (
        <>
            <Modal centered size="lg" show backdrop>
            {pendingResponse && <Loader />}
                <Modal.Header>
                    <Modal.Title>Search Results</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {validation.model && <div className="text-danger">{validation.model}</div>}
                    <div style={{ alignItems: 'center', minHeight: 100 }}>
                        {isInitialLoad && (
                            <div style={{ margin: 'auto', textAlign: 'center', width: 200 }}>
                                <Stack
                                    direction="horizontal"
                                    gap={2}
                                    style={{ alignItems: 'center' }}
                                >
                                    <span className="text-gray">Searching Cases...</span>
                                </Stack>
                            </div>
                        )}
                        {!isInitialLoad && (
                            <>
                                {caseSearchResults.length > 0 ? (
                                    <div>
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Case Name</th>
                                                    <th>Status</th>
                                                    <th>State</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {caseSearchResults.map(
                                                    (caseItem: ICaseModel, i: number) => {
                                                        return (
                                                            <tr key={i}>
                                                                <td>{caseItem.caseName}</td>
                                                                <td>
                                                                    {
                                                                        caseItem.caseStatus!
                                                                            .displayName!
                                                                    }
                                                                </td>
                                                                <td>{caseItem.state}</td>
                                                                <td className="col-sm-2">
                                                                    <button
                                                                        className="btn-no-bg text-gray"
                                                                        onClick={() => {
                                                                            props.onSelectCase(
                                                                                caseItem.guid!
                                                                            );
                                                                        }}
                                                                    >
                                                                        <i className="fal fa-check-square" />
                                                                        &nbsp;Select
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                        <CheckmatePagination
                                            pagedModel={pagedModel}
                                            onPageChange={(pageNumber: number) => {
                                                const computedLastRecordIndex =
                                                    (pageNumber - 1) * PageSize + PageSize;

                                                setPagedModel((prev) => ({
                                                    ...prev,
                                                    pageNumber,
                                                    recordEndIndex:
                                                        totalCaseCount < computedLastRecordIndex
                                                            ? totalCaseCount
                                                            : computedLastRecordIndex,
                                                    recordStartIndex:
                                                        (pageNumber - 1) * PageSize + 1,
                                                }));
                                            }}
                                            hidePageSizeSelector
                                        />
                                    </div>
                                ) : (
                                    <span>No results found!</span>
                                )}{' '}
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="dark" onClick={props.onCancel}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
